/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align */

import { first, tap }                     from 'rxjs/operators';
import { forkJoin, Observable }           from 'rxjs';

import {
  Blob as FileBlob,
  BlobType
}                                         from '../blob';

import { Project }                        from '../project';
import { AbstractProjectElement }         from './abstract.project.element';
import { ProjectElementType }             from './project.element.type';
import { PropertiesHolder }               from '../properties.holder';
import { Logger }                         from '@/util/logger';
import { empty }                          from '@/util/util';
import { IProjectBulkCallBack }           from '@/models/project/i.project.bulk.call.back';
import { ProjectElementFactoryDecorator } from '@/models/project/project.element.factory.decorator';
import { ProjectElementFactory }          from '@/models/project.element.factory';
import { CorridorBatchCreator }           from '@/helpers/corridor.batch.creator';
import { OperationalMeasureModelCreator } from '@/helpers/operational.measure.model.creator';
import { LocomotiveModelCreator }         from '@/helpers/locomotive.model.creator';
import { BatchRegressionCreator }         from '@/helpers/batch.regression.creator';
import { BatchSummaryCreator }            from '@/helpers/batch.summary.creator';
import { FileBrowserMetadata }            from '@/screens/ide/file.browser.metadata';



import * as $                             from 'jquery';
import 'gasparesganga-jquery-loading-overlay';


/**
 *
 * @class ProjectElement
 *
 * @brief the base class model for elements in a project.  Generally projects are a tree of elements,
 * we don't want to limit things too much, but we do want to provide the same structural support
 * everywhere, so that we can generalize the serialization of a project and also operations like
 * cloning a project.  Generally, we should be able to easily walk the element tree of any project
 * to have full query / modification ability.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
@ProjectElementFactoryDecorator('ProjectElement')
export class ProjectElement extends AbstractProjectElement {

  /** the project this element belongs to */
  public _project: Project             = null;

  /** unique id for this element in the project tree, this is how link parents or random access */
  public       _id:             string = '';

  /** parent element */
  public   _parent:     ProjectElement = null;

  /** when we do stuff in batch, mark nodes as shippable for triggering save (we save at end) */
  public _skipSave: boolean = false;

  /** child elements */
  public _children:   ProjectElement[] = [];

  /** the kind of element ths is (i.e. the sub-class) */
  public     _type: ProjectElementType = ProjectElementType.None;

  /** blob items have a blob type */
  public _blobType:           BlobType = BlobType.None;

  /** if this element wraps a blob, then we have to have a blob key */
  public  _blobKey:             string = '';

  /** version selector for blob */
  public _blobInstance:         string = 'latest';

  /** is this element enabled? */
  public  _enabled:            boolean = true;

  /** the label for this element */
  public    _label:             string = 'No Label';

  /** the icon for this element */
  public     _icon:             string = '';

  /** the class to use for the icon span */
  public _iconClass:            string = '';

  /** when a node is busy (long running operations) we allow there to be an override for icon styling. */
  public busyIconClass:             string = '';
  public busyNodeClass:             string = '';

  /** the extra class to use for the node overall */
  public _nodeClass:            string = '';

  /** is the node expanded to its children */
  public _expanded:            boolean = false;

  /** if provided, the kinds of valid children */
  public _acceptsElements: ProjectElementType[] = [];

  /** if provided, the kinds of blobs that are containable in this element */
  public    _acceptsBlobs:           BlobType[] = [];

  /** the kind of project element */
  public _elementKind:                   string = 'Project';

  /** when processing a bulk operation, this is the set of blob keys */
  protected pendingKeys: [string, string][]     = [];

  /** convert blob type to string */
  public static stringOfType(type: ProjectElementType) {

    switch (type) {
      case ProjectElementType.Simulation:              return 'simulation';
      case ProjectElementType.SimulationGroup:         return 'simulationgroup';
      case ProjectElementType.Manifest:                return 'manifest';
      case ProjectElementType.Inputs:                  return 'inputs';
      case ProjectElementType.Results:                 return 'results';
      case ProjectElementType.Snapshots:               return 'snapshots';
      case ProjectElementType.Dimension:               return 'dimension';
      case ProjectElementType.Blob:                    return 'blob';
      case ProjectElementType.Root:                    return 'root';
      case ProjectElementType.LineSimResult:           return 'linesimresult';
      case ProjectElementType.BatchSimResult:          return 'batchsimresult';
      case ProjectElementType.RegressionResult:        return 'regressionresult';
      case ProjectElementType.BatchSummaries:          return 'batchsummaries';
      case ProjectElementType.BatchMeasure:            return 'batchmeasure';
      case ProjectElementType.LineSimResultCalculated: return 'linesimresultcalculated';
      case ProjectElementType.CorridorGroup:           return 'corridorgroup';
      case ProjectElementType.KPI:                     return 'kpi';
      case ProjectElementType.KPIX:                    return 'kpix';
      case ProjectElementType.KPITable:                return 'kpitable';
      case ProjectElementType.LocomotiveModel:         return 'locomodel';
      case ProjectElementType.OperationalMeasures:     return 'opmeasure';
      case ProjectElementType.LocoMotiveModelResult:   return 'locomodelresult';
      case ProjectElementType.BatchGroup:              return 'batchgroup';
      case ProjectElementType.CorridorBatch:           return 'corridorbatch';
      case ProjectElementType.UserCommands:            return 'usercommands';
      case ProjectElementType.UserCommandsResult:      return 'usercommandsresult';
      case ProjectElementType.DiamondCrossings:        return 'diamondcrossings';
      case ProjectElementType.DiamondCrossingsResult:  return 'diamondcrossingsresult';
      case ProjectElementType.Property:                return 'property';
      case ProjectElementType.None:                    return 'none';

    }

    return 'none';
  }

  /** convert blob type string to blob type */
  public static typeofString(text: string) : ProjectElementType {

    text = text.toLowerCase().trim();

    switch (text) {
      case 'analysis':                return ProjectElementType.Simulation;
      case 'simulation':              return ProjectElementType.Simulation;
      case 'simulationgroup':
      case 'analysisgroup':           return ProjectElementType.SimulationGroup;
      case 'manifest':                return ProjectElementType.Manifest;
      case 'inputs':                  return ProjectElementType.Inputs;
      case 'results':                 return ProjectElementType.Results;
      case 'snapshots':               return ProjectElementType.Snapshots;
      case 'dimension':               return ProjectElementType.Dimension;
      case 'blob':                    return ProjectElementType.Blob;
      case 'root':                    return ProjectElementType.Root;
      case 'linesimresult':           return ProjectElementType.LineSimResult;
      case 'batchsimresult':          return ProjectElementType.BatchSimResult;
      case 'regressionresult':        return ProjectElementType.RegressionResult;
      case 'batchsummaries':          return ProjectElementType.BatchSummaries;
      case 'batchmeasure':            return ProjectElementType.BatchMeasure;
      case 'linesimresultcalculated': return ProjectElementType.LineSimResultCalculated;
      case 'corridorgroup':           return ProjectElementType.CorridorGroup;
      case 'kpi':                     return ProjectElementType.KPI;
      case 'kpix':                    return ProjectElementType.KPIX;
      case 'kpitable':                return ProjectElementType.KPITable;
      case 'locomodel':               return ProjectElementType.LocomotiveModel;
      case 'opmeasure':               return ProjectElementType.OperationalMeasures;
      case 'locomodelresult':         return ProjectElementType.LocoMotiveModelResult;
      case 'batchgroup':              return ProjectElementType.BatchGroup;
      case 'corridorbatch':           return ProjectElementType.CorridorBatch;
      case 'usercommands':            return ProjectElementType.UserCommands;
      case 'usercommandsresult':      return ProjectElementType.UserCommandsResult;
      case 'diamondcrossings':        return ProjectElementType.DiamondCrossings;
      case 'diamondcrossingsresult':  return ProjectElementType.DiamondCrossingsResult;
      case 'property':                return ProjectElementType.Property;
      case 'none':                    return ProjectElementType.None;
    }

    return ProjectElementType.None;
  }

  /** standard constructor, all sub-classes must call super() */
  constructor(
          project: Project,
               id: string,
             type: ProjectElementType,
        blobType?: BlobType,
         blobKey?: string,
    blobInstance?: string
  ) {

    super();

    this._project = project;
    this._id      = id;
    this._type    = type;

    if (blobType) {

      this._blobType = blobType;
    }

    if (blobKey) {

      this._blobKey = blobKey;
    }

    if (blobInstance) {

      this._blobInstance = blobInstance;
    }
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.newsimulation = {
      name: 'New Simulation',
      icon: 'fas fa-plus',
      disabled: this.isBusy()
    }

    actionSet.newlocomodel = {
      name: 'New Locomotive Model',
      icon: 'fas fa-plus',
      disabled: this.isBusy()
    }

    actionSet.newmeasures = {
      name: 'New Operating Measures',
      icon: 'fas fa-plus',
      disabled: this.isBusy()
    }

    actionSet.newregression = {
      name: 'New Regression',
      icon: 'fas fa-plus',
      disabled: this.isBusy()
    }

    actionSet.newcorridorbatch = {
      name: 'Import Corridor Batch',
      icon: 'fas fa-plus',
      disabled: this.isBusy()
    }

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.renameproject = {
      name: 'Rename Project',
      icon: 'fas fa-tag',
      disabled: this.isBusy()
    }

    actionSet.deleteproject = {
      name: 'Delete Project',
      icon: 'fas fa-trash',
      disabled: this.isBusy()
    };

    return actionSet;
  }

  /** test if this item might have children (or does) */
  public isFolder() : boolean {

    return this._children.length !== 0;
  }

  /** test if the node is expanded in the presentation */
  public isExpanded() : boolean {

    return this._expanded;
  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    return false;
  }

  /** refresh the icon display for this element */
  public updateIcon(alternate: string=''): void {

    if (!alternate) {

      /* by default, the icon should be the icon used for that blob type */

      this._icon    = 'blob-icons/' + FileBlob.iconOfType(this._blobType);

    } else {

      /* use the icon they gave us */

      this._icon    = 'blob-icons/' + alternate;
    }

    this.redraw()
  }

  /**
   * test if this node is marked as skippable for triggering a project save.
   * this helps us avoid doing endless saves when we do big batch operations on
   * lots of nodes.
   */
  public canSkipSave() {

    return this._skipSave;
  }

  /** mark the node as skippable (or not) for triggering a save.  */
  public skipSave(flag: boolean) {

    this._skipSave = flag;
  }

  /** find depth from root for this element */
  public rank() : number {

    let rank:            number = 0;
    let pointer: ProjectElement = this._parent;

    while (pointer) {

      rank++;

      pointer = pointer._parent;
    }

    return rank;
  }

  /** order the child array if there is ordering to the children */
  public orderChildren() {

    if (this._children.length === 0) {

      return;
    }

    /* just want alphabetical */

    this._children.sort((a, b) => {

      if (a._label < b._label) {
        return -1;
      }

      if (a._label > b._label) {
        return 1;
      }

      return 0;
    });

  }

  /** FancyTree style JSON  render of this element and its children */
  public render(event, data) {

    /* build up the child data if we hae children... */

    const cOut = [];

    this.orderChildren();

    for (const c of this._children) {

      cOut.push(c.render(event, data));
    }

    /* pass back the simplified JSON style structure */
    return {
               key: this._id,
              icon: this._icon,
             title: this._label,
            folder: this.isFolder(),
          expanded: this._expanded,
          children: cOut,
      extraClasses: this._nodeClass
    };
  }

  /** fetch the immediate parent */
  public parent() : ProjectElement {

    return this._parent;
  }

  /** fetch immediate children */
  public children() : ProjectElement[] {

    return this._children;
  }

  /** fetch all parents */
  public ancestors() : ProjectElement[] {

    const results: ProjectElement[] = [];
    let         p: ProjectElement = this;

    /* walk the parent chain to build parent list */

    while (p._parent !== null) {

      results.push(p._parent);

      p = p._parent;
    }

    return results;
  }

  /** fetch all possible children */
  public descendants(type?: ProjectElementType) : ProjectElement[] {

    const results: ProjectElement[] = [];
    let     stack: ProjectElement[] = [];

    stack.push(this);

    while (stack.length !== 0) {

      const item: ProjectElement = stack.pop();

      if (type) {

        if (type === item._type) {

          results.push(item);
        }

      } else {

        results.push(item);
      }
      stack = stack.concat(item._children);
    }

    return results;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    /*
     * if you provide a node as input, then local details will add to and override
     * what is already there.
     */

    /*
     * we expect a given sub-class to implement node() and fromNode so that they
     * can be serialized and deserialized.
     */

    const base = {
                id: this._id,
            parent: this._parent ? this._parent._id : null,
              type: ProjectElement.stringOfType(this._type),
          blobType: FileBlob.stringOfType(this._blobType),
           blobKey: this._blobKey,
      blobinstance: this._blobInstance,
           enabled: this._enabled,
             label: this._label,
          expanded: this._expanded,
              icon: this._icon,
         iconClass: this._iconClass,
         nodeClass: this._nodeClass
    };

    return init ? {...init, ...base } : base;
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    /*
     * we expect a given sub-class to implement node() and fromNode so that they
     * can be serialized and deserialized.
     */

    for (const todo of this._children) {

      this._project.elements.delete(todo._id);
    }

    this._parent   = null;
    this._children = [];

    /* pick out any attributes we can assign to self, sub-classes pick out their own stuff */

    this._id           = node.id;
    this._type         = ProjectElement.typeofString(node.type);
    this._blobType     = FileBlob.typeofString(node.blobType);
    this._blobKey      = node.blobKey;
    this._blobInstance = 'latest';
    this._enabled      = node.enabled;
    this._label        = node.label;
    this._icon         = node.icon;
    this._expanded     = node.expanded;
    this._iconClass    = '';
    this._nodeClass    = '';

    if (node?.iconClass) {

      this._iconClass = node.iconClass;
    }

    if (node?.nodeClass) {

      this._nodeClass = node.nodeClass;
    }

    if (node?.blobinstance) {

      this._blobInstance = node.blobinstance;
    }

    if ((this._icon === '') || (this._blobType !== BlobType.None)) {

      this._icon     = 'blob-icons/' + FileBlob.iconOfType(this._blobType);
    }

  }

  /** fetch compatible blob type, for some elements this means what its cast to. */
  public blobType(): BlobType {

    return this._blobType;
  }

  /** test if this element can be moved around in the project structure */
  public isMovable(): boolean {

    return false;
  }

  /** can this element be a drop site of drag and drop? */
  public acceptsDrop(data): boolean {

    return false;
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return true;
  }

  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    properties.title = this._label;

    if (/\//.test(this._icon) || /(\.png)|(\.gif)|(\.jpg)/.test(this._icon)) {

      /* make sure its prefixed with assets/images/ */

      if ( !/^\/?assets\/images\//.test(this._icon)) {

        properties.icon = `<img style="height: 18px; width: auto;" src="/assets/images/${this._icon}">`;

      } else {

        properties.icon = `<img style="height: 18px; width: auto;" style="size: 16px; src="${this._icon}">`;

      }

    } else {

      properties.icon = `<i class="${this._icon}"></i>`;
    }

    properties.set('Name',    this._label);
    properties.set('Kind',    this._elementKind);
    properties.set('ID',      this._id);
    properties.set('Enabled', this._enabled ? 'True' : 'False');

    if (this._blobKey && this._blobKey !== '') {

      properties.set('Blob',     this._blobKey);
      properties.set('Instance', this._blobInstance);
      properties.set('Type',     FileBlob.stringOfType(this._blobType));
    }

    return properties;
  }

  /** fetch base name of the element */
  public basename(key?: string): string {

    /* the name should come from the blob key/path */

    /* @link https://stackoverflow.com/questions/3820381/need-a-basename-function-in-javascript */

    let base: string = this._blobKey;

    if ( empty(base) ) {

      base = this._label;
    }

    if (key) {

      base = key;
    }

    base = base.substring(base.lastIndexOf('/') + 1);

    if (base.lastIndexOf('.') !== -1) {

      base = base.substring(0, base.lastIndexOf('.'));
    }

    return base;
  }

  /*
   * when blobs get moved/copied in bulk, project elements have to allow being told where
   * their blobs have been moved to.
   *
   */
  public adjusted(searchKey: string, replaceKey: string) {

    if (this._blobKey === searchKey) {

      this._blobKey = replaceKey;
    }

  }

  /** helper to do a bulk search and replace on any descendants to update blob keys. */
  public shiftBlobs(searchKey: string,
                    replaceKey: string,
                    finalize: IProjectBulkCallBack,
                    excludes: ProjectElement[] = []) {

    const blobMap: Map<string, ProjectElement> = new Map<string, ProjectElement>();
    const    kids: ProjectElement[]            = this.descendants();

    // @ts-ignore
    $.LoadingOverlay('show', {

      text: 'Calculating renaming of children...',
      progress: 0,
      background: 'rgba(255, 255, 255, 0.85)',
      textColor: 'black',
      imageColor: 'black'
    });

    /* find any descendants whose blob key needs to be updated... */

    kids.forEach((kid) => {

      /*
       * in some cases the descendants might include an item that gets both
       * key twitching and a name change, but the name (_label) hasn't actually
       * been updated yet (it will during fainalize() )...so...we have to allow
       * for excludes, where in such cases, the caller will tell us which
       * elements to skip because the caller is handling them at the end.
       *
       */

      if (excludes.filter(x => x._id === kid._id).length > 0) {

        /* skip this one, it will be handled by the caller */

        return ;
      }

      /* add in the primary blob */

      if (!blobMap.has(kid._blobKey)) {

        if (kid._blobKey.indexOf(searchKey) >= 0) {

          blobMap.set(kid._blobKey, kid);
        }
      }

      /* add in any extra blobs to twitch */

      kid.secondaryBlobs().forEach((key: string) => {

        if (!blobMap.has(key)) {

          if (key.indexOf(searchKey) >= 0) {

            blobMap.set(key, kid);
          }
        }
      });

    });

    Logger.debug(`[ProjectElement] shifting to '${searchKey}' for blobs: `, Array.from(blobMap.keys()));

    /* create  a bulk move ... */

    const observables:    any[] = [];
    const     expired: string[] = [];
    const     cleanup: string[] = [];

    for (const key of blobMap.keys()) {

      /*
       * figure out the destination, the source is the old blob key, the destination is
       * the adjusted path.
       *
       */

      const  src: string = key;
      const  dst: string = src.replace(searchKey, replaceKey);
      const name: string = blobMap.get(key)._label;

      /* on success we'll unlink the old one */

      expired.push(key);

      /* create the request to copy ... */

      observables.push( this._project.services.blobber.copy(src, dst, 'latest', name).pipe(first()) );

      /* on failure, we will cleanup any attempted copies, and revert to what we were at before. */

      cleanup.push(dst);
    }

    // @ts-ignore
    $.LoadingOverlay('text', `Renaming...`);

    if (expired.length === 0) {

      /* nothing to do */

      // @ts-ignore
      $.LoadingOverlay('hide');

      finalize(true);

      return ;
    }

    /* do it! */

    const N: number = expired.length;
    let   n: number = 0;

    forkJoin(
      observables.map(o => o.pipe(tap(() => {

        n++;

        const progress: string = ((n/N)*100).toFixed(2);

        // @ts-ignore
        $.LoadingOverlay('text', `[${progress}%] Renaming...`);

      })))).subscribe(

      () => {

        // @ts-ignore
        $.LoadingOverlay('hide');

        /* tell the caller everything is finished */

        finalize(true);

        /* all were moved, adjust the keys for all, and save the project */

        Logger.debug(`[ProjectElement] (shiftBlobs) copy finished, adjusting keys ...`);

        for (const [key, el] of blobMap) {

          const dst: string = key.replace(searchKey, replaceKey);

          Logger.debug(`[ProjectElement] (shiftBlobs) adjusting ${key} => ${dst}...`);

          el.adjusted(key, dst);
        }

        /* unlink the old locations ... */

        Logger.debug(`[ProjectElement] (shiftBlobs) unlinking the old locations...`);

        const todo:    any[] = [];

        expired.forEach((item: string) => {

          todo.push( this._project.services.blobber.remove(item).pipe(first()) );

        });

        forkJoin(todo).subscribe(
          () => {

            Logger.debug(`[ProjectElement] (shiftBlobs) old locations cleared.`, expired);

            finalize(true);

          }, (error) => {

            finalize(error);

            Logger.error(`[ProjectElement] (shiftBlobs) could not clean old locations: `, expired);
          })


      }, (error) => {

        // @ts-ignore
        $.LoadingOverlay('hide');

        Logger.error(`[ProjectElement] (shiftBlobs) failed to copy all blobs to new locations: `, error);

        /* tell the caller everything is finished */

        finalize(error);

        /* unlink any we attempted to move */

        const todo:    any[] = [];

        cleanup.forEach((item: string) => {

          todo.push( this._project.services.blobber.remove(item).pipe(first()) );

        });

        forkJoin(todo).subscribe(
          () => {

            Logger.debug(`[ProjectElement] (shiftBlobs) cleaned up attempted copies.`, cleanup);

          }, () => {

            Logger.error(`[ProjectElement] (shiftBlobs) could not clean up attempted copies: `, cleanup);
          })

      });

  }

  /** helper to download blobs to the user */
  public dowloadBlob(blobType: BlobType, key: string, instance: string = 'latest', name: string = '') {

    Logger.debug('[ProjectElement] downloading', key, blobType);

    let   mime: string   = 'application/octet-stream';
    let    txt: boolean  = false;

    if (empty(name)) {

      name = this.basename(key);
    }

    /*
     * everything gets downloaded as binary data, but if it reasonably looks like JSON
     * data, download it as such.
     *
     */

    if (/\.json$/i.test(key) || /\.json$/i.test(name)) {

      mime = 'application/json';
      txt  = true;

    } else if (/\.csv/i.test(key) || /\.csv$/i.test(name)) {

      mime = 'text/csv';
      txt  = true;
    }

    /* fetch the blob */

    this._project.services.blobber.contents(key, 'latest', txt ? 'text' : 'blob')
      .pipe(first())
      .subscribe(
        (content: any) => {

          Logger.debug(`[ProjectElement] downloading ${name} => ${mime}`);

          let blob: Blob;

          if (typeof content === 'string') {

            /* it was presumed to be text */

            blob = new Blob([content], {type: mime});

          } else {

            /* its taken as a binary blob */

            blob = content as Blob;
          }

          const url  = window.URL.createObjectURL(blob);

          /* setup a fake one-time link with the right download name and the blob attached as the download */

          const element: HTMLAnchorElement = document.createElement('a');
          element.href          = url;
          element.download      = name || 'download';

          element.style.display = 'none';

          document.body.appendChild(element);

          /* do it */

          element.click();

          /* clean up */

          document.body.removeChild(element);

        },
        (error) => {

          Logger.error(`[ProjectElement] can't find content ${key}: `, error);
        });
  }

  /** fetch any secondary blobs for this element */
  public secondaryBlobs(): string[] {

    return [];
  }

  /**
   * some elements might have longer running or asynchronous tasks that need to be done as part of
   * deletion.  To allow them to do <whatever>; similar to secondaryBlobs(), we ask nodes if they
   * have deletion tasks, and if they do, we just merge these in with the observables we're
   * creating anyway to do things like clean up blobs tied to elements.  Then we we forkJoin()
   * the whole merged result.
   */
  public deletionTasks(): Observable<any>[] {

    return [];
  }

  /** delete the sub-tree(s) implied by the selected elements, can include intermediate nodes. */
  public deleteElements(selection: ProjectElement[], force: boolean = false, extraBlobs: string[] = []) {

    const pe: ProjectElement = this;

    if (pe.pendingKeys.length > 0) {

      /* we're already doing a batch */

      return ;
    }

    /*
     * if any of the input selection is the root of the project, then this is really a project
     * delete.
     *
     */

    if (selection.filter(x => x._type == ProjectElementType.Root).length > 0) {

      Logger.debug(`[ProjectElement] implied project delete.`);

      pe._project.services.projects.deleteProject();

      return ;
    }

    let todo:  ProjectElement[];

    /* quick lookup of what's in the selection */

    const selectMap: Map<string, ProjectElement> = new Map<string, ProjectElement>();

    selection.forEach((item) => {

      if (!selectMap.has(item._id)) {

        selectMap.set(item._id, item);
      }
    });

    /* filter out Simulation, Project and Root, they only go away when whole project goes away */

    if (!force) {

      /*
       * filter out Simulation, Inputs and Results, they are built in structure, to delete them,
       * you have to delete a parent of them (SimulationGroup or above),
       *
       */

      todo = selection.filter((pe: ProjectElement) => {

        return !((pe._type === ProjectElementType.Root) ||
          (pe._type === ProjectElementType.Simulation) ||
          (pe._type === ProjectElementType.Inputs) ||
          (pe._type === ProjectElementType.Results));

      });

    } else {

      /* we're going to delete everything */

      todo = [...selection];
    }

    // @ts-ignore
    $.LoadingOverlay('show', {

      text: 'Calculating blobs to delete...',
      progress: 0,
      background: 'rgba(255, 255, 255, 0.85)',
      textColor: 'black',
      imageColor: 'black'
    });

    Logger.debug(`[ProjectElement] deleting selection: `, todo);

    /*
     * find all the blobs we'll have to delete, we need to process those as a fork/join batch,
     * since they have to be deleted asynchronously.
     *
     */

    const   blobMap: Map<string, ProjectElement> = new Map<string, ProjectElement>();

    todo.forEach((item) => {

      item.descendants().forEach((kid) => {

        /* already queued for removal? */

        if (blobMap.has(kid._id)) {

          return ;
        }

        blobMap.set(kid._id, kid);
      });

    });

    /*
     * now figure out which elements we are deleting from the overall tree; we want the
     * the items in the selection, and their children, even if some of their children aren't
     * selected, because selecting something higher up means "delete all of it".  also, we
     * ensure that we don't delete things are structural, such as "-> Inputs" and "<- Results",
     * unless a parent above them is also selected.  Normally you can't delete structural stuff,
     * but if you delete their parents, the whole thing is going away, so just do it.
     *
     * Finally, the top levels Root / Project / Simulation can not deleted. They only go away
     * when the project as a whole goes away. So when we get the "selection, we remove anything
     * that is Simulation, Project or Root.
     *
     * To delete the whole project, we will have a separate feature in the project window or elsewhere
     * that will just call deleteElements via the root element, with all elements of the project.  We
     * want to ensure project deletion is separate because its obvious a lot tougher to recover from
     * if you didn't really mean to delete the project.
     *
     */

    const children:  Map<string, ProjectElement> = new Map<string, ProjectElement>();

    todo.forEach((item) => {

      item.descendants().forEach((kid) => {

        /* are we already going to delete it? */

        if (blobMap.has(kid._id)) {

          /* will deleted by blob delete */

          return;
        }

        /* if it's not in the selection, then one of its parents must be, so we have implied deletion */

        if (!kid.isDeletable() || !selectMap.has(kid._id)) {

          /* check for parent that is being deleted, that supersedes a child not being deletable */

          let deletingParent: boolean = false;

          kid.ancestors().forEach((i) => {

            if (selectMap.has(i._id)) {

              deletingParent = true;
            }

          });

          if (!deletingParent) {

            return ;
          }
        }

        if (children.has(kid._id)) {

          /* already queued */

          return ;
        }

        /* this one is deletable! */

        children.set(kid._id, kid);

      });

    });

    /*
     * make sure the children (the intermediates) are in the right order we want lowest children first,
     * so that as they get published by Project.deleteElements(), we never have a child being published
     * after its parent has already been deleted.
     *
     * Basically we have to sort them by rank/depth from root.
     *
     */

    let intermediate: ProjectElement[] = [].concat(Array.from(children.values()), Array.from(blobMap.values()));

    intermediate.forEach((item) => {

      item._children = [];
    });

    intermediate.sort((a: ProjectElement, b: ProjectElement) : number => {

      return a.rank() - b.rank();
    });

    /* de-duplicate! */

    intermediate = intermediate.filter((item, pos) => intermediate.indexOf(item) === pos);

    /*
     * do the asynchronous deletes; these are elements that we have to ask the blob
     * manager to delete (network call).
     *
     */

    Logger.debug(`[ProjectElement] deleting elements: `, intermediate);

    /* let's go to work!  delete the blobs */

    const pendingMap: Map<string,string> = new Map<string,string>();
    let   projDelete: boolean            = false;

    extraBlobs.forEach((item) => {

      if (pendingMap.has(item)) {

        return ;
      }

      pendingMap.set(item, 'latest');
    });

    intermediate.forEach((leaf: ProjectElement) => {

      /*
       * the secondary blob(s) of the element, some elements are more beefy, they have
       * additional blobs they manage.
       *
       */

     leaf.secondaryBlobs().forEach((otherBlob: string) => {

       if (!otherBlob || otherBlob === '') {

         return ;
       }

       if (pendingMap.has(otherBlob)) {

         return ;
       }

       pendingMap.set(otherBlob, 'latest');
     });

      /* the primary blob of the element (if there is one) */

      if (!leaf._blobKey || leaf._blobKey === '') {

        return ;
      }

      if (pendingMap.has(leaf._blobKey)) {

        return ;
      }

      pendingMap.set(leaf._blobKey, leaf._blobInstance);
    });

    /* de-duplicate! */

    for (const [key, instance] of pendingMap) {

      pe.pendingKeys.push([key, instance]);

      if (key === pe._project.key) {

        projDelete            = true;
        pe._project.deleted   = true;

        Logger.debug('[ProjectElement] project deletion detected.');

      }
    }

    /*
     * the simplest form of deletion is just blobs that are linked to nodes in the tree.
     * however, for some nodes (for example corridor batches), there is a lot more to do.
     * There can be some kind of more general deletion work/task required, and it might
     * by asynchronous...it might need to be executed server siide.
     *
     * To allow for more complex delete actions per node, we ask the nodes in focus
     * for any deletion observables, which we can then merge with our blob deletion
     * observables, and forkJoin the whole mess together for a single batch of work
     * to do progress on.
     *
     */

    let complexDeletions: Observable<any>[] = [];

    intermediate.forEach((complex: ProjectElement) => {

      const taskList: Observable<any>[] = complex.deletionTasks();

      if (taskList.length === 0) {

        return ;
      }

      complexDeletions = complexDeletions.concat(taskList);
    });

    /* if we ended up with nothing to actually delete..we're done :) */

    if ((pe.pendingKeys.length === 0) && (complexDeletions.length === 0)) {

      /* now unlink any intermediate stuff */

      intermediate = intermediate.concat(Array.from(blobMap.values()));
      intermediate = intermediate.filter((item, pos) => intermediate.indexOf(item) === pos);

      pe._project.deleteElements(intermediate, true);

      /* make sure the project is saved */

      pe.pendingKeys = [];

      /* except if we're in the process of deleting it */

      if (projDelete == false) {

        pe.save();
      }

      // @ts-ignore
      $.LoadingOverlay('hide');

      Logger.debug('[ProjectElement] non-blob delete completed.');

      return ;
    }

    /* build out the blob deletion observables ... */

    Logger.debug('[ProjectElement] deleting blobs by key: ', pe.pendingKeys);

    /* create the observables for copying blobs... */

    const observables: any[] = complexDeletions;

    for (const f of pe.pendingKeys) {

      /* as we copy we are also forcing the type to be whatever the user selected as the import type */

      observables.push( pe._project.services.blobber.remove(f[0], f[1]).pipe(first()) );
    }

    // @ts-ignore
    $.LoadingOverlay('text', `Deleting...Please wait, this may take a couple of minutes...`);

    /* now fork & join to do them all in parallel */

    let N: number = observables.length;
    let n: number = 0;

    pe.busy();

    forkJoin(observables.map(o => o.pipe(tap(() => {

      n++;

      const progress: string = ((n/N)*100).toFixed(2);

      // @ts-ignore
      $.LoadingOverlay('text', `[${progress}%] Deleting...`);

    })))).subscribe(
      (results) => {

        pe.unBusy();

        // @ts-ignore
        $.LoadingOverlay('text', `Deleted!`);

        /* everything got deleted ok, unlink ...*/

        Logger.debug('[ProjectElement] blobs deleted: ', results);

        /* now unlink any intermediate stuff */

        intermediate = intermediate.concat(Array.from(blobMap.values()));
        intermediate = intermediate.filter((item, pos) => intermediate.indexOf(item) === pos);

        pe._project.deleteElements(intermediate, true);

        /* make sure the project is saved */

        pe.pendingKeys = [];

        /* except if we're in the process of deleting it */

        if (projDelete == false) {

          pe.save();
        }

        // @ts-ignore
        $.LoadingOverlay('hide');
      },
      (error) => {

        pe.unBusy();

        // @ts-ignore
        $.LoadingOverlay('hide');

        Logger.error('[ProjectElement] problems deleting blobs: ', error);

      });

  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!['newsimulation', 'newlocomodel', 'newmeasures', 'newregression', 'newbatchsummary', 'newcorridorbatch', 'deleteproject', 'renameproject', 'viewinbrowser'].find(x => x === key);
  }

  /** convenience to rename the project */
  public renameProject() {

    this.action('renameproject', [this]);
  }

  /** sub-classes must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    const pe: ProjectElement = this;

    switch (key) {

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(pe._project.key, pe._project.key,));

        break;
      }

      case 'newsimulation': {

        Logger.debug('[ProjectElement] adding new Simulation Group...');

        /* find the simulation group, create one if we have to. */

        let parent: ProjectElement = pe._project.simulation();

        /* go through a dialog to get the name for the simulation group */

        pe._project.services.modals.open(
          'new-simulation-modal',
          (name, resolve, reject) => {

            Logger.debug('[ProjectElement] (new sim) validating: ', name);

            /* is it a valid name? */

            if (name.trim() === '') {

              reject('Simulation name can not be empty.');
            }

            /* do we already have a simulation of this name? */

            const peers: ProjectElement[] = parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Simulation of that name already exists in project.');
              }

            }

            /* its golden! */

            resolve(name);
          },
          (name) => {

            /* add the new child simulation */

            Logger.debug('[ProjectElement] new simulation: ',  name);

            ProjectElementFactory.fromObject(ProjectElementType.SimulationGroup, {
              project:  pe._project,
              name,
              parent,
              publish:  true
            });

            /* make sure the thing we just created is visible */

            pe._project.expand([parent]);

            /* make sure the project is saved */

            pe.save();

        });

      }
      break;

      case 'newlocomodel': {

        /* let the model creation helper do its thing ... */

        new LocomotiveModelCreator(pe._project);

      }
      break;

      case 'newmeasures': {

          /* let the model creation helper do its thing ... */

          new OperationalMeasureModelCreator(pe._project);

        }
        break;

      case 'newregression': {

          /* let the model creation helper do its thing ... */

          new BatchRegressionCreator(pe._project);

        }
        break;

      case 'newbatchsummary': {

          /* let the model creation helper do its thing ... */

          new BatchSummaryCreator(pe._project);

        }
        break;

      case 'newcorridorbatch': {

        new CorridorBatchCreator(pe._project);
      }
      break;

      case 'renameproject': {

        /* go through a dialog to get the new name */

        pe._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            Logger.debug('[ProjectElement] validating: ', name);

            /* is it a valid name? */

            if (name.trim() === '') {

              reject('Project name name can not be empty.');

              return ;
            }

            /* do we already have a project of this name? */

            const userName: string = pe._project.services.projects.user().username
            const   newKey: string = pe._project.defaultProjectFile(userName, name);

            Logger.debug(`[ProjectElement] checking for clash of project key u=${userName}, n=${name}, k=${newKey}`);

            pe._project.services.blobber.info(newKey)
              .pipe(first())
              .subscribe(
                (meta: any) => {

                  if (meta) {

                    Logger.error(`[ProjectElement] project of that name already exists.`);

                    reject('Project of that name already exists.');

                    return ;

                  } else {

                    /* doesn't exist, we're ok */

                    resolve(name);
                  }
                },
                () => {

                  /* doesn't exist, we're ok */

                  resolve(name);
                });

          },
          (name) => {

            /* do the actual renaming... */

            Logger.debug('[ProjectElement] renaming... ',  name);

            pe._project.services.projects.renameProject(name);

          }, {
            title: 'Rename Project',
            placeholder: this._label
          });
      }
      break;

      case 'deleteproject': {

        /* Project will delete the element tree and the project */

        this._project.services.projects.deleteProject();

      }
      break;

    }

  }

  /** test if any blob of this type can be a child of this element */
  public acceptsBlobType(type: BlobType): boolean {

    if (this._acceptsBlobs.length === 0) {

      return true;
    }

    return !!this._acceptsBlobs.find(x => x === type);
  }

  /** test if any project element of the given type could be a child of this element */
  public acceptsElement(type: ProjectElementType): boolean {

    if (this._acceptsElements.length === 0) {

      return true;
    }

    return !!this._acceptsElements.find(x => x === type);
  }

  /** save the parent project (and everything in it including this element) */
  public save() {

    Logger.debug(`[ProjectElement] triggering save... `);

    this._project.dirty = true;

    this._project.save();
  }

  /** convenience to refresh own appearance in the presentation */
  public redraw() {

    this._project.redrawElements([this]);
  }

  /** all are notified (this method) after they are hooked into the project tree */
  public onAdded(): void {

  }

  /** check if this element or any of its children are currently busy. */
  public isBusy(): boolean {

    const kids: ProjectElement[] = this.descendants();
    let   busy: boolean = false;

    kids.push(this);

    kids.forEach((kid) => {

      if (this._project.busyElements.has(kid._id)) {

        busy = true;
      }

    });

    return busy;
  }

  /** add self ot the busy set */
  public busy() {

    this.busyIconClass = 'fancytree-custom-icon-yellow';
    this.busyNodeClass = 'element-pending';

    this._project.busyElements.set(this._id, this);

    this.redraw();
  }

  /** remove self from the busy set */
  public unBusy() {

    this.busyIconClass = '';
    this.busyNodeClass = '';

    this._project.busyElements.delete(this._id);

    this.redraw();
  }
}
